/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { FaultyIntentDetails } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/FaultyIntentDetails';
import { SideBar } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/SideBar/SideBar';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

export type IntentsWithErrorsCardProps = {
    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId, filters: PassedFilters) => void;
};

/**
 * Displays the faulty intents with some recommendations and details
 * about the selected one.
 */
export const IntentsWithErrorsCard = observer((props: IntentsWithErrorsCardProps) => {
    const { onIntentClick } = props;
    const [selectedIntent, setSelectedIntent] = React.useState<IntentMetadata>(null);
    const { dispatcher, rootStore } = useGlobalState();

    React.useEffect(() => {
        if (selectedIntent) {
            dispatcher.dispatch('loadFaultyIntent', { id: selectedIntent.intentId });
        }
    }, [selectedIntent]);

    const { analyticsStore } = rootStore.currentLuisAppStore.currentAppVersionStore;
    const sortedIntent = Array.from(analyticsStore.utterancesPerIntent.values()).sort((a, b) => (a.numUtterances < b.numUtterances ? -1 : 1));

    const intentTrainingResult = selectedIntent && analyticsStore.intentsTrainingResult.get(selectedIntent.intentId)
            ? analyticsStore.intentsTrainingResult.get(selectedIntent.intentId)
            : null;

    return (
        <Stack horizontal>
            <SideBar
                selectedIntent={selectedIntent}
                onSelectedIntentChange={setSelectedIntent}
                loadIntentTrainingResult={intentId => dispatcher.dispatch('loadFaultyIntent', { id: intentId })}
            />
            <FaultyIntentDetails
                onIntentClick={onIntentClick}
                selectedIntent={selectedIntent}
                mostNeedyIntent={sortedIntent[0]}
                mostGreedyIntent={sortedIntent[sortedIntent.length - 1]}
                meanNumberOfUtterances={analyticsStore.utterancesCount / analyticsStore.intentsCount}
                unclearConflictedIntents={intentTrainingResult ? intentTrainingResult.unclearWith : []}
                incorrectlyConflictedIntents={intentTrainingResult ? intentTrainingResult.incorrectWith : []}
                isNeedyIntent={
                    selectedIntent && analyticsStore.needyIntents.map(intent => intent.intentId).indexOf(selectedIntent.intentId) !== -1
                }
                isGreedyIntent={
                    selectedIntent && analyticsStore.greedyIntents.map(intent => intent.intentId).indexOf(selectedIntent.intentId) !== -1
                }
            />
        </Stack>
    );
});
